import { useState, useEffect, useCallback, useRef } from 'react';
import { Save, ChevronDown, ChevronUp, Telescope, Target, AlertCircle, CheckCircle2, Plus, X, Lightbulb, ArrowDownToLine, BookMarked } from 'lucide-react';
import { supabase } from '../services/supabase';
import { logger } from '../services/logger';
import { analyzeJournalEntry } from '../services/openai';
import { generateSmartPrompts, detectTags, expandEntry, extractActionItems } from '../services/journal-ai';

interface UnifiedJournalEntryProps {
  date?: Date;
  onSave?: () => void;
  coachingTopicId?: string;
  promptText?: string;
}

interface JournalInsight {
  insights: string[];
  recommendations: string[];
  patterns_detected: string[];
  energy_score: number;
  progress_score: number;
  suggested_actions: string[];
}

interface SmartPrompt {
  id: string;
  text: string;
  category: string;
  source?: string;
}

export function UnifiedJournalEntry({ date = new Date(), onSave, coachingTopicId, promptText }: UnifiedJournalEntryProps) {
  const [mode, setMode] = useState<'quick' | 'detailed'>('quick');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [showInsights, setShowInsights] = useState(false);

  const [quickEntry, setQuickEntry] = useState('');
  const [winOfDay, setWinOfDay] = useState('');
  const [challengeFaced, setChallengeFaced] = useState('');
  const [whatWorked, setWhatWorked] = useState('');
  const [whatToImprove, setWhatToImprove] = useState('');
  const [actionItems, setActionItems] = useState<string[]>([]);
  const [newAction, setNewAction] = useState('');
  const [weeklyPlanRef, setWeeklyPlanRef] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [energyLevel, setEnergyLevel] = useState(5);

  const [insights, setInsights] = useState<JournalInsight | null>(null);
  const [previousEntries, setPreviousEntries] = useState<any[]>([]);

  const [smartPrompts, setSmartPrompts] = useState<SmartPrompt[]>([]);
  const [loadingPrompts, setLoadingPrompts] = useState(true);
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [goDeeper, setGoDeeper] = useState<string | null>(null);
  const [loadingDeeper, setLoadingDeeper] = useState(false);
  const [detectedActions, setDetectedActions] = useState<string[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const AVAILABLE_TAGS = ['stress', 'sleep', 'focus', 'energy', 'courage', 'conversations', 'work', 'health', 'relationships', 'growth'];

  useEffect(() => {
    loadPreviousEntries();
    loadSmartPrompts();
    if (promptText) {
      setQuickEntry(promptText);
      setMode('detailed');
    }
  }, [date, promptText]);

  async function loadSmartPrompts() {
    setLoadingPrompts(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoadingPrompts(false);
        return;
      }
      const prompts = await generateSmartPrompts(user.id);
      setSmartPrompts(prompts);
    } catch (err) {
      logger.error('Error loading smart prompts:', err);
    } finally {
      setLoadingPrompts(false);
    }
  }

  const analyzeText = useCallback((text: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      const detected = detectTags(text);
      setSuggestedTags(detected.filter(t => !tags.includes(t)));

      const actions = extractActionItems(text);
      setDetectedActions(actions.filter(a => !actionItems.includes(a)));
    }, 500);
  }, [tags, actionItems]);

  function handleQuickEntryChange(text: string) {
    setQuickEntry(text);
    analyzeText(text);
    if (goDeeper) setGoDeeper(null);
  }

  async function handleGoDeeper() {
    if (quickEntry.length < 30) return;
    setLoadingDeeper(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const question = await expandEntry(quickEntry, user.id);
      setGoDeeper(question);
    } catch (err) {
      logger.error('Error getting deeper question:', err);
    } finally {
      setLoadingDeeper(false);
    }
  }

  function selectPrompt(prompt: SmartPrompt) {
    setSelectedPrompt(prompt.text);
    setQuickEntry('');
  }

  function addSuggestedTag(tag: string) {
    if (!tags.includes(tag)) {
      setTags([...tags, tag]);
      setSuggestedTags(suggestedTags.filter(t => t !== tag));
    }
  }

  function addDetectedAction(action: string) {
    if (!actionItems.includes(action)) {
      setActionItems([...actionItems, action]);
      setDetectedActions(detectedActions.filter(a => a !== action));
    }
  }

  async function loadPreviousEntries() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('journal_entries')
        .select('*, journal_insights(*)')
        .eq('user_id', user.id)
        .eq('date', date.toISOString().split('T')[0])
        .order('created_at', { ascending: false })
        .limit(3);

      setPreviousEntries(data || []);
    } catch (err: any) {
      logger.error('Error loading previous entries:', err);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const entryData: any = {
        user_id: user.id,
        date: date.toISOString().split('T')[0],
        entry_type: mode,
        quick_entry: quickEntry.trim() || null,
        win_of_day: winOfDay.trim() || null,
        challenge_faced: challengeFaced.trim() || null,
        what_worked: whatWorked.trim() || null,
        what_to_improve: whatToImprove.trim() || null,
        action_items: actionItems.length > 0 ? actionItems : [],
        tags: tags,
        energy_level: energyLevel,
        coaching_topic_id: coachingTopicId || null,
        weekly_plan_reference: weeklyPlanRef.trim() || null,
        prompt_source: promptText || null,
        mood: 'neutral',
        created_at: new Date().toISOString()
      };

      const { data: savedEntry, error: saveError } = await supabase
        .from('journal_entries')
        .insert(entryData)
        .select()
        .single();

      if (saveError) throw saveError;

      if (savedEntry && (quickEntry.trim() || mode === 'detailed')) {
        setAnalyzing(true);
        try {
          const aiAnalysis = await analyzeJournalEntry({
            quickEntry: quickEntry.trim(),
            winOfDay: winOfDay.trim(),
            challengeFaced: challengeFaced.trim(),
            whatWorked: whatWorked.trim(),
            whatToImprove: whatToImprove.trim(),
            energyLevel,
            mood: 'neutral'
          });

          const progressScore = mode === 'detailed' ?
            Math.min(95, aiAnalysis.energyScore + 10) :
            aiAnalysis.energyScore;

          const { error: insightError } = await supabase
            .from('journal_insights')
            .insert({
              journal_entry_id: savedEntry.id,
              user_id: user.id,
              insights: aiAnalysis.insights || [],
              recommendations: aiAnalysis.recommendations || [],
              patterns_detected: [],
              energy_score: aiAnalysis.energyScore,
              progress_score: progressScore,
              suggested_actions: [],
              coaching_topic_suggestions: []
            });

          if (!insightError) {
            setInsights({
              insights: aiAnalysis.insights || [],
              recommendations: aiAnalysis.recommendations || [],
              patterns_detected: [],
              energy_score: aiAnalysis.energyScore,
              progress_score: progressScore,
              suggested_actions: []
            });
            setShowInsights(true);
          }

          await supabase
            .from('journal_entries')
            .update({ is_ai_analyzed: true })
            .eq('id', savedEntry.id);

        } catch (analysisError) {
          logger.error('AI analysis error:', analysisError);
        } finally {
          setAnalyzing(false);
        }
      }

      resetForm();
      await loadPreviousEntries();
      onSave?.();

      const successMessage = document.createElement('div');
      successMessage.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successMessage.textContent = 'Journal entry saved successfully';
      document.body.appendChild(successMessage);
      setTimeout(() => document.body.removeChild(successMessage), 3000);

    } catch (err: any) {
      logger.error('Error saving journal entry:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setQuickEntry('');
    setWinOfDay('');
    setChallengeFaced('');
    setWhatWorked('');
    setWhatToImprove('');
    setActionItems([]);
    setWeeklyPlanRef('');
    setTags([]);
    setEnergyLevel(5);
    setMode('quick');
    setSelectedPrompt(null);
    setGoDeeper(null);
    setSuggestedTags([]);
    setDetectedActions([]);
    loadSmartPrompts();
  }

  function addAction() {
    if (newAction.trim()) {
      setActionItems([...actionItems, newAction.trim()]);
      setNewAction('');
    }
  }

  function removeAction(index: number) {
    setActionItems(actionItems.filter((_, i) => i !== index));
  }

  function toggleTag(tag: string) {
    setTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  }

  const hasQuickContent = quickEntry.trim().length > 0;
  const canExpandToDetailed = mode === 'quick' && hasQuickContent;

  return (
    <div className="bg-white rounded-xl shadow-sm">
      {error && (
        <div className="mx-6 mt-6 bg-red-50 text-red-800 p-4 rounded-lg text-sm flex items-center">
          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
          {error}
        </div>
      )}

      {previousEntries.length > 0 && (
        <div className="p-6 border-b border-gray-100 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Earlier Today</h3>
          <div className="space-y-2">
            {previousEntries.map((entry, index) => (
              <div key={index} className="bg-white rounded-lg p-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">
                    {new Date(entry.created_at).toLocaleTimeString()}
                  </span>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                    {entry.entry_type}
                  </span>
                </div>
                <p className="text-gray-700">{entry.quick_entry || entry.win_of_day}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {showInsights && insights && (
        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-electric-400/5 to-gold-500/5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Telescope className="h-5 w-5 text-electric-400 mr-2" />
              <h3 className="text-lg font-semibold text-ink-950">AI Insights</h3>
            </div>
            <button onClick={() => setShowInsights(false)} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-white rounded-lg p-3">
              <div className="text-sm text-gray-600 mb-1">Energy Score</div>
              <div className="text-2xl font-bold text-electric-400">{insights.energy_score}/100</div>
            </div>
            <div className="bg-white rounded-lg p-3">
              <div className="text-sm text-gray-600 mb-1">Progress Score</div>
              <div className="text-2xl font-bold text-success-500">{insights.progress_score}/100</div>
            </div>
          </div>

          {insights.insights.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Key Insights</h4>
              <ul className="space-y-1">
                {insights.insights.map((insight, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-success-500 mr-2 flex-shrink-0 mt-0.5" />
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {insights.recommendations.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Recommendations</h4>
              <ul className="space-y-1">
                {insights.recommendations.map((rec, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start">
                    <Target className="h-4 w-4 text-gold-500 mr-2 flex-shrink-0 mt-0.5" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {!quickEntry && !selectedPrompt && smartPrompts.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <Lightbulb className="h-4 w-4 mr-2 text-amber-500" />
              <span>Start with a prompt:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {loadingPrompts ? (
                <div className="text-sm text-gray-500">Loading prompts...</div>
              ) : (
                smartPrompts.map((prompt) => (
                  <button
                    key={prompt.id}
                    type="button"
                    onClick={() => selectPrompt(prompt)}
                    className="px-3 py-2 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg text-sm text-gray-700 transition-colors text-left"
                  >
                    {prompt.text}
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {mode === 'quick' ? 'Quick Log' : 'Detailed Entry'}
          </label>
          {selectedPrompt && (
            <div className="mb-2 p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-start justify-between">
              <div className="flex items-start">
                <BookMarked className="h-4 w-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-amber-800">{selectedPrompt}</span>
              </div>
              <button
                type="button"
                onClick={() => setSelectedPrompt(null)}
                className="text-amber-600 hover:text-amber-800 ml-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          <textarea
            value={quickEntry}
            onChange={(e) => handleQuickEntryChange(e.target.value)}
            rows={mode === 'quick' ? 2 : 4}
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-gold-500 focus:ring-gold-500 resize-none"
            placeholder={selectedPrompt ? 'Your response...' : (mode === 'quick' ? 'One honest line about today...' : 'What happened today? Be specific...')}
          />
        </div>

        {quickEntry.length >= 30 && !goDeeper && mode === 'quick' && (
          <button
            type="button"
            onClick={handleGoDeeper}
            disabled={loadingDeeper}
            className="flex items-center text-sm text-amber-600 hover:text-amber-700 transition-colors"
          >
            {loadingDeeper ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-600 border-t-transparent mr-2" />
                Thinking...
              </>
            ) : (
              <>
                <ArrowDownToLine className="h-4 w-4 mr-1" />
                Go deeper with AI
              </>
            )}
          </button>
        )}

        {goDeeper && (
          <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
            <div className="flex items-start">
              <Telescope className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-800 mb-1">Reflect deeper:</p>
                <p className="text-sm text-gray-700">{goDeeper}</p>
              </div>
            </div>
          </div>
        )}

        {suggestedTags.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center text-sm text-gray-600">
              <Telescope className="h-4 w-4 mr-2 text-amber-500" />
              <span>Suggested tags:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestedTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => addSuggestedTag(tag)}
                  className="px-3 py-1 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-full text-sm font-medium transition-colors flex items-center"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}

        {detectedActions.length > 0 && (
          <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-3">
            <div className="flex items-center text-sm font-medium text-green-800">
              <Target className="h-4 w-4 mr-2" />
              <span>Actions detected - track them?</span>
            </div>
            <div className="space-y-2">
              {detectedActions.map((action, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => addDetectedAction(action)}
                  className="w-full p-2 bg-white hover:bg-green-50 border border-green-200 rounded-lg text-sm text-left text-gray-700 flex items-center justify-between transition-colors"
                >
                  <span>{action}</span>
                  <Plus className="h-4 w-4 text-green-600" />
                </button>
              ))}
            </div>
          </div>
        )}

        {mode === 'quick' && canExpandToDetailed && (
          <button
            type="button"
            onClick={() => setMode('detailed')}
            className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gold-500 hover:text-gold-500 transition-colors flex items-center justify-center"
          >
            <ChevronDown className="h-4 w-4 mr-2" />
            Expand for detailed reflection
          </button>
        )}

        {mode === 'detailed' && (
          <>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Win of the Day
                  <span className="text-gray-500 text-xs ml-1">(What went well?)</span>
                </label>
                <textarea
                  value={winOfDay}
                  onChange={(e) => setWinOfDay(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-gold-500 focus:ring-gold-500 resize-none"
                  placeholder="Today's win was..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Challenge Faced
                  <span className="text-gray-500 text-xs ml-1">(What was hard?)</span>
                </label>
                <textarea
                  value={challengeFaced}
                  onChange={(e) => setChallengeFaced(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-gold-500 focus:ring-gold-500 resize-none"
                  placeholder="The challenge I faced was..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What Worked
                  <span className="text-gray-500 text-xs ml-1">(What should I keep doing?)</span>
                </label>
                <textarea
                  value={whatWorked}
                  onChange={(e) => setWhatWorked(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-gold-500 focus:ring-gold-500 resize-none"
                  placeholder="What worked well..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What to Improve
                  <span className="text-gray-500 text-xs ml-1">(What needs to change?)</span>
                </label>
                <textarea
                  value={whatToImprove}
                  onChange={(e) => setWhatToImprove(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-gold-500 focus:ring-gold-500 resize-none"
                  placeholder="Tomorrow I will..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Action Items
                  <span className="text-gray-500 text-xs ml-1">(Specific next steps)</span>
                </label>
                {actionItems.length > 0 && (
                  <ul className="space-y-1 mb-2">
                    {actionItems.map((action, i) => (
                      <li key={i} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-700">{action}</span>
                        <button
                          type="button"
                          onClick={() => removeAction(i)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newAction}
                    onChange={(e) => setNewAction(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAction())}
                    placeholder="Add an action..."
                    className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-gold-500 focus:ring-gold-500"
                  />
                  <button
                    type="button"
                    onClick={addAction}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Energy Level: {energyLevel}/10
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={energyLevel}
                  onChange={(e) => setEnergyLevel(parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => setMode('quick')}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
            >
              <ChevronUp className="h-4 w-4 mr-1" />
              Collapse to quick mode
            </button>
          </>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
          <div className="flex flex-wrap gap-2">
            {AVAILABLE_TAGS.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  tags.includes(tag)
                    ? 'bg-gold-500 text-ink-950'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading || analyzing || (!quickEntry.trim() && mode === 'quick')}
            className="flex items-center px-6 py-3 bg-gold-500 text-ink-950 rounded-lg hover:bg-gold-500/90 transition-colors disabled:opacity-50 font-semibold"
          >
            {loading || analyzing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-ink-950 border-t-transparent mr-2" />
                {analyzing ? 'Analyzing...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Entry
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
